/**
 * Solution Theme Configuration
 * 
 * Auto-generated from brand/brand-config.json
 * DO NOT hardcode colors - modify brand-config.json instead
 * 
 * Supports multiple brand colors:
 * - Primary: Main Fluent UI theme (buttons, links, navigation)
 * - Secondary: Supporting elements (badges, secondary actions)
 * - Tertiary: Additional accents (highlights, special features)
 * - Accent: Simple colors without variant scales
 * 
 * @see brand/README.md for setup instructions
 * @see https://react.fluentui.dev/?path=/docs/theme-theme-designer
 */

import { 
  createLightTheme, 
  createDarkTheme, 
  BrandVariants,
  Theme 
} from '@fluentui/react-components';

// Import brand configuration
// Note: Ensure tsconfig.json has "resolveJsonModule": true
import brandConfig from '../../brand/brand-config.json';

// =============================================================================
// PRIMARY BRAND COLOR (Fluent UI Theme)
// =============================================================================

/**
 * Primary brand color variants from brand-config.json
 * Used for: Primary CTAs, navigation highlights, links
 * 
 * Scale positions:
 * - 10-40: Darkest (text on light backgrounds)
 * - 50-70: Dark (hover states, borders)
 * - 80: BASE primary color
 * - 90-110: Medium (secondary elements)
 * - 120-160: Lightest (backgrounds, subtle fills)
 */
const primaryBrandColors: BrandVariants = {
  10: brandConfig.colors.primary.variants['10'],
  20: brandConfig.colors.primary.variants['20'],
  30: brandConfig.colors.primary.variants['30'],
  40: brandConfig.colors.primary.variants['40'],
  50: brandConfig.colors.primary.variants['50'],
  60: brandConfig.colors.primary.variants['60'],
  70: brandConfig.colors.primary.variants['70'],
  80: brandConfig.colors.primary.variants['80'],  // ← Primary brand color
  90: brandConfig.colors.primary.variants['90'],
  100: brandConfig.colors.primary.variants['100'],
  110: brandConfig.colors.primary.variants['110'],
  120: brandConfig.colors.primary.variants['120'],
  130: brandConfig.colors.primary.variants['130'],
  140: brandConfig.colors.primary.variants['140'],
  150: brandConfig.colors.primary.variants['150'],
  160: brandConfig.colors.primary.variants['160'],
};

/**
 * Light theme with primary brand colors
 */
export const lightTheme: Theme = createLightTheme(primaryBrandColors);

/**
 * Dark theme with primary brand colors
 */
export const darkTheme: Theme = createDarkTheme(primaryBrandColors);

// =============================================================================
// SECONDARY & TERTIARY BRAND COLORS
// =============================================================================

/**
 * Secondary brand color palette
 * Used for: Secondary actions, accent elements, badges
 */
export const secondaryColors = brandConfig.colors.secondary ? {
  hex: brandConfig.colors.secondary.hex,
  name: brandConfig.colors.secondary.name,
  usage: brandConfig.colors.secondary.usage,
  // Common usage variants
  base: brandConfig.colors.secondary.variants['80'],
  hover: brandConfig.colors.secondary.variants['70'],
  pressed: brandConfig.colors.secondary.variants['60'],
  light: brandConfig.colors.secondary.variants['140'],
  lighter: brandConfig.colors.secondary.variants['160'],
  dark: brandConfig.colors.secondary.variants['40'],
  // Full scale for advanced usage
  variants: brandConfig.colors.secondary.variants,
} : null;

/**
 * Tertiary brand color palette
 * Used for: Additional accents, highlights, special features
 */
export const tertiaryColors = brandConfig.colors.tertiary ? {
  hex: brandConfig.colors.tertiary.hex,
  name: brandConfig.colors.tertiary.name,
  usage: brandConfig.colors.tertiary.usage,
  // Common usage variants
  base: brandConfig.colors.tertiary.variants['80'],
  hover: brandConfig.colors.tertiary.variants['70'],
  pressed: brandConfig.colors.tertiary.variants['60'],
  light: brandConfig.colors.tertiary.variants['140'],
  lighter: brandConfig.colors.tertiary.variants['160'],
  dark: brandConfig.colors.tertiary.variants['40'],
  // Full scale for advanced usage
  variants: brandConfig.colors.tertiary.variants,
} : null;

/**
 * Accent colors (simple hex values without variant scales)
 * Used for: Charts, data visualization, category coding
 */
export const accentColors = brandConfig.colors.accent || [];

// =============================================================================
// CSS CUSTOM PROPERTIES
// =============================================================================

/**
 * Inject brand CSS custom properties into :root
 * Call this once at app initialization for global CSS access
 * 
 * @example
 * // In index.tsx or App.tsx
 * injectBrandCSSProperties();
 * 
 * // Then use in CSS
 * .my-secondary-badge {
 *   background-color: var(--brand-secondary);
 * }
 */
export function injectBrandCSSProperties(): void {
  const root = document.documentElement;
  
  // Primary
  root.style.setProperty('--brand-primary', brandConfig.colors.primary.hex);
  root.style.setProperty('--brand-primary-hover', brandConfig.colors.primary.variants['70']);
  root.style.setProperty('--brand-primary-light', brandConfig.colors.primary.variants['140']);
  
  // Secondary
  if (brandConfig.colors.secondary) {
    root.style.setProperty('--brand-secondary', brandConfig.colors.secondary.hex);
    root.style.setProperty('--brand-secondary-hover', brandConfig.colors.secondary.variants['70']);
    root.style.setProperty('--brand-secondary-light', brandConfig.colors.secondary.variants['140']);
  }
  
  // Tertiary
  if (brandConfig.colors.tertiary) {
    root.style.setProperty('--brand-tertiary', brandConfig.colors.tertiary.hex);
    root.style.setProperty('--brand-tertiary-hover', brandConfig.colors.tertiary.variants['70']);
    root.style.setProperty('--brand-tertiary-light', brandConfig.colors.tertiary.variants['140']);
  }
  
  // Accent colors
  brandConfig.colors.accent?.forEach((color, index) => {
    root.style.setProperty(`--brand-accent-${index + 1}`, color.hex);
  });
}

// =============================================================================
// SOLUTION TOKENS
// =============================================================================

/**
 * Solution-specific semantic tokens
 * Maps brand colors to consistent UI patterns
 */
export const solutionTokens = {
  // Primary brand (from Fluent theme)
  brandPrimary: lightTheme.colorBrandBackground,
  brandPrimaryHover: lightTheme.colorBrandBackgroundHover,
  brandPrimaryText: lightTheme.colorBrandForeground1,
  
  // Secondary brand (custom)
  brandSecondary: secondaryColors?.base || lightTheme.colorPalettePurpleBackground2,
  brandSecondaryHover: secondaryColors?.hover || lightTheme.colorPalettePurpleBackground2,
  brandSecondaryLight: secondaryColors?.light || lightTheme.colorPalettePurpleBackground2,
  
  // Tertiary brand (custom)
  brandTertiary: tertiaryColors?.base || lightTheme.colorPaletteTealBackground2,
  brandTertiaryHover: tertiaryColors?.hover || lightTheme.colorPaletteTealBackground2,
  brandTertiaryLight: tertiaryColors?.light || lightTheme.colorPaletteTealBackground2,
  
  // Status indicators
  statusActive: lightTheme.colorPaletteGreenForeground1,
  statusInactive: lightTheme.colorNeutralForeground3,
  statusPending: lightTheme.colorPaletteYellowForeground1,
  statusError: lightTheme.colorPaletteRedForeground1,
  
  // Navigation
  navBackground: lightTheme.colorNeutralBackground1,
  navItemActive: lightTheme.colorBrandBackground2,
  navItemHover: lightTheme.colorNeutralBackground1Hover,
  
  // Data visualization (uses accent colors if available)
  chartPrimary: brandConfig.colors.primary.hex,
  chartSecondary: secondaryColors?.base || lightTheme.colorPaletteBlueForeground2,
  chartTertiary: tertiaryColors?.base || lightTheme.colorPaletteTealForeground2,
  chartAccent1: accentColors[0]?.hex || lightTheme.colorPaletteYellowForeground2,
  chartAccent2: accentColors[1]?.hex || lightTheme.colorPaletteRedForeground2,
  chartAccent3: accentColors[2]?.hex || lightTheme.colorPalettePinkForeground2,
  chartAccent4: accentColors[3]?.hex || lightTheme.colorPaletteGreenForeground2,
};

// =============================================================================
// CATEGORY COLORS
// =============================================================================

/**
 * Solution category colors from brand-config.json
 * Used for: Visual category differentiation across the platform
 *
 * Import these instead of hardcoding hex values in components.
 */
export const categoryColors = {
  compliance: (brandConfig as any).categoryThemes?.compliance?.primary ?? '#13B5B1',
  code: (brandConfig as any).categoryThemes?.code?.primary ?? '#E852B9',
  environment: (brandConfig as any).categoryThemes?.environment?.primary ?? '#E89652',
  risk: (brandConfig as any).categoryThemes?.risk?.primary ?? '#E85252',
  ai: (brandConfig as any).categoryThemes?.ai?.primary ?? '#8852E0',
} as const;

// =============================================================================
// SEMANTIC COLORS
// =============================================================================

/**
 * Semantic status colors from brand-config.json (success/warning/error/info).
 * Used for: status icons, badges, inline indicators — anywhere a raw hex would
 * otherwise be hardcoded to signal success/warning/error/info state.
 */
export const semanticColors = {
  success: brandConfig.colors.semantic.success,
  warning: brandConfig.colors.semantic.warning,
  error: brandConfig.colors.semantic.error,
  info: brandConfig.colors.semantic.info,
} as const;

// =============================================================================
// F044 VERDICT SEMANTIC TOKENS
// =============================================================================

/**
 * F044 Verdict, Voice & Score Architecture — the RESERVED semantic colour tokens every verdict
 * surface must colour by (`ColorIntent`), and ONLY by. Red is exclusively `verdictFailure`; green
 * is exclusively `verdictPositive`. Null trends ("+0"), unset ("--"/"Not Set"), and states are
 * ALWAYS `verdictNeutral` grey — never green, never red. Consumed by `src/components/verdict/**`;
 * no page should reach past this map for a verdict/trend/state colour.
 */
export const verdictTokens = {
  /** States, null trends, unset values, zero counts. Never green, never red. */
  verdictNeutral: lightTheme.colorNeutralForeground3,
  verdictNeutralBackground: lightTheme.colorNeutralBackground3,

  /** A genuine positive verdict over a NON-ZERO denominator only. */
  verdictPositive: semanticColors.success,
  verdictPositiveBackground: lightTheme.colorPaletteGreenBackground2,

  /** Constrained / approval-required / degraded-but-working. */
  verdictCaution: semanticColors.warning,
  verdictCautionBackground: lightTheme.colorPaletteYellowBackground2,

  /** A genuine failure / violation / block only. */
  verdictFailure: semanticColors.error,
  verdictFailureBackground: lightTheme.colorPaletteRedBackground2,
} as const;

export type VerdictTokens = typeof verdictTokens;

// =============================================================================
// BRAND METADATA
// =============================================================================

/**
 * Brand metadata from config
 */
export const brandMeta = {
  name: brandConfig.brand.name,
  shortName: brandConfig.brand.shortName,
  tagline: brandConfig.brand.tagline,
  description: brandConfig.brand.description,
};

/**
 * Brand color info (for UI pickers, documentation, etc.)
 */
export const brandColorInfo = {
  primary: {
    hex: brandConfig.colors.primary.hex,
    name: brandConfig.colors.primary.name,
    usage: brandConfig.colors.primary.usage,
  },
  secondary: secondaryColors ? {
    hex: secondaryColors.hex,
    name: secondaryColors.name,
    usage: secondaryColors.usage,
  } : null,
  tertiary: tertiaryColors ? {
    hex: tertiaryColors.hex,
    name: tertiaryColors.name,
    usage: tertiaryColors.usage,
  } : null,
  accents: accentColors.map(c => ({
    hex: c.hex,
    name: c.name,
    usage: c.usage,
  })),
};

/**
 * Logo paths from config
 */
export const brandLogos = {
  primary: brandConfig.assets.logo.primary,
  light: brandConfig.assets.logo.light,
  dark: brandConfig.assets.logo.dark,
  icon: brandConfig.assets.logo.icon,
};

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type SolutionTheme = typeof lightTheme;
export type SolutionTokens = typeof solutionTokens;
export type BrandColorInfo = typeof brandColorInfo;
